import { motion } from "framer-motion";
import { ArrowRight, Video, Users, Lightbulb } from "lucide-react";
import { CONCERT_BG, KAJABI } from "@/lib/data";

const pillars = [
 {
 icon: Users,
 title: "Vetted Pros In The Room",
 body: "You collaborate with pro-producers and writers already working at a high level. Not beginners, not hobbyists.",
 },
 {
 icon: Lightbulb,
 title: "2 Active Supervisors",
 body: "Two working music supervisors give real insight and creative direction before each day. So you're writing toward what actually gets placed.",
 },
 {
 icon: Video,
 title: "100% Virtual",
 body: "No flights. No hotels. Just deep, focused sessions from your own studio. Structured to move songs from 'good idea' to sync solution.",
 },
];

export default function Camp() {
 return (
 <div data-testid="camp-page">
 <section
 data-testid="camp-hero"
 className="relative overflow-hidden grain border-b border-ink-600"
 >
 <div
 className="absolute inset-0 bg-cover bg-center"
 style={{ backgroundImage: `url(${CONCERT_BG})` }}
 />
 <div className="absolute inset-0 bg-gradient-to-b from-ink-900/85 via-ink-900/85 to-ink-900" />

 <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-32 md:pt-40 pb-24">
 <motion.div
 initial={{ opacity: 0, y: 16 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ duration: 0.6 }}
 className="grid lg:grid-cols-12 gap-10 items-center"
 >
 <div className="lg:col-span-7 order-2 lg:order-1">
 <div className="inline-flex items-center gap-2 bg-amber/10 border border-amber/40 text-amber font-mono text-xs uppercase tracking-widest px-3 py-1.5 rounded-sm">
 Virtual Sync Camp · Est. 2023
 </div>
 <h1 className="font-display text-6xl md:text-8xl font-bold leading-[0.9] tracking-tight mt-6">
 <span className="text-amber">UM</span>
 <span className="inline-block w-4 md:w-6" aria-hidden="true" />
 <span className="text-amber italic">C.A.M.P.</span>
 </h1>
 <p
 data-testid="camp-acronym"
 className="mt-6 font-mono text-xs md:text-sm uppercase tracking-[0.3em] text-white/55"
 >
 Collaboration · Access · Music · Placement
 </p>
 <p className="mt-8 text-xl md:text-2xl text-white/85 max-w-2xl leading-snug">
 You won't be figuring this out alone.
 </p>
 <p className="mt-4 text-lg text-white/70 max-w-2xl leading-relaxed">
 A virtual sync camp where you collaborate with vetted
 pro-producers and writers already working at a high level - 
 with{" "}
 <span className="text-amber font-semibold">
 two active music supervisors
 </span>{" "}
 giving real insight and direction before your experience each
 day. This is the level where songs stop sounding like
 "good ideas" and start sounding like{" "}
 <span className="italic text-amber">solutions.</span>
 </p>
 </div>

 <div className="lg:col-span-5 order-1 lg:order-2 flex justify-center lg:justify-end">
 <motion.img
 initial={{ opacity: 0, scale: 0.9, rotate: -3 }}
 animate={{ opacity: 1, scale: 1, rotate: 0 }}
 transition={{ duration: 0.8, delay: 0.2 }}
 src="/um-camp-logo.png"
 alt="UM CAMP patch"
 data-testid="camp-logo-hero"
 className="w-full max-w-sm md:max-w-md aspect-square object-contain drop-shadow-[0_0_40px_rgba(34,197,94,0.15)]"
 />
 </div>
 </motion.div>
 </div>
 </section>

 <section
 data-testid="camp-pillars"
 className="relative bg-ink-900 py-24 md:py-32"
 >
 <div className="max-w-7xl mx-auto px-4 md:px-8">
 <div className="mb-14 max-w-3xl">
 <p className="tag-kicker">What makes C.A.M.P. different</p>
 <h2 className="font-display text-4xl md:text-5xl font-bold tracking-normal leading-[1.15] mt-3">
 Where songs stop being good ideas and start being{" "}
 <span className="italic text-amber">solutions.</span>
 </h2>
 </div>

 <div className="grid md:grid-cols-3 gap-5">
 {pillars.map((p, i) => (
 <motion.div
 key={i}
 initial={{ opacity: 0, y: 20 }}
 whileInView={{ opacity: 1, y: 0 }}
 viewport={{ once: true }}
 transition={{ duration: 0.5, delay: i * 0.1 }}
 data-testid={`camp-pillar-${i}`}
 className="p-8 border border-ink-600 bg-ink-800 hover:border-amber/40 transition-colors"
 >
 <p.icon size={28} className="text-amber" />
 <h3 className="font-display text-2xl font-bold mt-5">
 {p.title}
 </h3>
 <p className="mt-3 text-white/65 text-sm leading-relaxed">
 {p.body}
 </p>
 </motion.div>
 ))}
 </div>
 </div>
 </section>

 <section
 data-testid="camp-cta"
 className="relative bg-ink-800 py-24 md:py-32 border-y border-ink-600"
 >
 <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
 <p className="tag-kicker">Ready for the next C.A.M.P.?</p>
 <h2 className="font-display text-4xl md:text-6xl font-bold tracking-normal leading-[1.15] mt-5">
 Get the full <span className="text-amber italic">details</span>.
 </h2>
 <p className="mt-6 text-lg text-white/70 max-w-2xl mx-auto">
 Dates, pricing, format and how to join. All on our full C.A.M.P.
 page.
 </p>

 <a
 href={KAJABI.camp}
 target="_blank"
 rel="noopener noreferrer"
 data-testid="camp-landing-cta"
 className="mt-10 inline-flex items-center gap-3 bg-amber hover:bg-amber-hover text-black font-bold uppercase tracking-widest text-sm px-8 py-5 rounded-sm transition-all group"
 >
 Go to the C.A.M.P. page
 <ArrowRight
 size={16}
 className="transition-transform group-hover:translate-x-1"
 />
 </a>
 </div>
 </section>
 </div>
 );
}
